import { Component, Input, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { TranslationService } from '../../../../../../shared/translations/services/translation.service';
import { WorkOrderService } from '../../../../services/workorder.service';
import { DealerState } from '../../../../../../shared/state/DealerState';
import { Dealer } from '../../../../../../shared/dealer/models/dealer';
import { UpdateLocationInformationAction, WorkOrderState } from '../../../../../../shared/state/WorkOrderState';
import { WorkOrderLineView } from '../../../work-order-line-list/model/work-order-line-view.model';

@Component({
  selector: 'app-location-information-tab',
  templateUrl: './location-information-tab.component.html',
  styleUrls: ['./location-information-tab.component.scss'],
  standalone: false
})
export class LocationInformationTabComponent {
  @Input() workOrderLine: WorkOrderLineView | null = null;
  public workOrderService = inject(WorkOrderService);
  public store = inject(Store);
  public route = inject(ActivatedRoute);
  public translationService = inject(TranslationService);
  public formBuilder = inject(FormBuilder);

  dealer: Dealer;
  workOrderLineId: string;
  workOrderHeaderId: string;
  isLoading = false;
  locationDetailsForm: FormGroup;
  shopFloorUpdatesForm: FormGroup;
  posUpdatesForm: FormGroup;
  packlistInformationForm: FormGroup;

  private readonly POST_TRANSLATION_KEYS: { [key: string]: string } = {
    BUILDER: 'builderText',
    CHAMBER: 'chamberText',
    REPAIR: 'repairText',
    INITIAL_INSPECTION: 'initialInspectionText',
    FILLING: 'fillingText',
    BUFFER: 'bufferText',
    REJECT: 'actionRejectText',
    ADJUSTMENT: 'adjustmentText',
    SKIVE: 'skiveText',
    FINAL_INSPECTION: 'finalInspectionText'
  };

  public getTranslatedPost(post?: string): string {
    if (!post) return '';
    const key = (post || '').trim().toUpperCase().replace(/\s+/g, '_');
    const translationKey = this.POST_TRANSLATION_KEYS[key];
    return translationKey ? this.translationService.translate(translationKey) : post;
  }

  constructor() {
    this.workOrderLineId = this.route.snapshot.paramMap.get('workOrderLineId');
    this.workOrderHeaderId = this.route.snapshot.paramMap.get('workOrderId');
    this.locationDetailsForm = this.formBuilder.group({
      casingLocation: [''],
      currentPost: [''],
      plantRetreaded: ['']
    });

    this.shopFloorUpdatesForm = this.formBuilder.group({
      CIAResult: [''],
      xRaysResult: [''],
      adjPercent: [''],
      adjReason: [''],
      finalSpeedIndex: [''],
      minTreadDepth: [''],
      maxTreadDepth: [''],
      circumference: [''],
      width: [''],
      treadDesc: ['']
    });

    this.posUpdatesForm = this.formBuilder.group({
      POSResults: [''],
      invoiceDate: [''],
      invoiceNumber: ['']
    });

    this.packlistInformationForm = this.formBuilder.group({
      packList: [''],
      packListStatus: ['']
    });
  }

  loadData(): void {
    this.isLoading = true;
    this.locationDetailsForm.reset();
    this.shopFloorUpdatesForm.reset();
    this.posUpdatesForm.reset();
    this.packlistInformationForm.reset();

    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.workOrderService
      .fetchWorkOrderLineLocationInfo(this.dealer.id, this.workOrderLineId, this.workOrderHeaderId)
      .subscribe((locationInfo) => {
        const model = this.mapToLocationInformationModel(locationInfo?.data);
        this.store.dispatch(new UpdateLocationInformationAction(model));
        this.isLoading = false;
      });

    this.store.select(WorkOrderState.locationInformation).subscribe((locationInfo) => {
      if (locationInfo) {
        this.locationDetailsForm.patchValue(locationInfo.locationDetails);
        this.shopFloorUpdatesForm.patchValue(locationInfo.shopFloorUpdates);
        this.posUpdatesForm.patchValue(locationInfo.posUpdates);
        this.packlistInformationForm.patchValue(locationInfo.packlistInformation);
      }
    });
  }

  mapToLocationInformationModel = (apiResponse: any) => ({
    locationDetails: {
      casingLocation: apiResponse.shippingLocation,
      currentPost: apiResponse.currentPost,
      plantRetreaded: apiResponse.plantRetread
    },
    shopFloorUpdates: {
      CIAResult: apiResponse.ciaResults ? (apiResponse.ciaResults === 'Y' ? 'Pass' : 'Fail') : null,
      xRaysResult: apiResponse.xrayResults ? (apiResponse.xrayResults === 'Y' ? 'Pass' : 'Fail') : null,
      adjPercent: apiResponse.adjPercent,
      adjReason: apiResponse.adjReason,
      finalSpeedIndex: apiResponse.finalSpeedIndex,
      minTreadDepth: apiResponse.treadDepthMin,
      maxTreadDepth: apiResponse.treadDepthMax,
      circumference: apiResponse.buffCircum,
      width: apiResponse.width,
      // Tread design is not returned by the location-info API; source it from the work order line
      treadDesc: this.workOrderLine?.treadDesc
    },
    posUpdates: {
      POSResults: apiResponse.posResults,
      invoiceDate: apiResponse.invoiceDate || null,
      invoiceNumber: apiResponse.invoiceNumber
    },
    packlistInformation: {
      packList: apiResponse.packListNumber,
      packListStatus: apiResponse.packListStatus
    }
  });

  display(): boolean {
    return this.workOrderLine?.processCode === 'AD';
  }
}
